import React, { useState, useEffect } from 'react';
import { useGetIdentity, useTranslate } from 'react-admin';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

import Background from './Background';
import DialogBox from './DialogBox';
import Enemy from './Enemy';
import Ally from './Ally';
import HealthBar from './HealthBar';
import useSkills from './useSkills';
import animationsSx from './animationsSx';


const Solo = ({ stats = [], workingDaysCount }) => {
    const [loading, setLoading] = useState(true);
    const [steps, setSteps] = useState([]);
    const [currentStep, setCurrentStep] = useState();
    const [text, setText] = useState('');
    const [hp, setHp] = useState({ projects: {}, user: 100 });
    const [flashAnimation, setFlashAnimation] = useState(false);
    const [attackAnimation, setAttackAnimation] = useState(false);
    const { data: account } = useGetIdentity();
    const translate = useTranslate();
    const skills = useSkills();

    useEffect(() => {
        const fetchStats = async () => {
            if (!stats) {
                return;
            }

            setLoading(true);

            let projectsHp = {};
            const { data } = stats;
            let defaultSteps = [];
            let lastStepText = '';
            if (data?.length > 0) {
                defaultSteps = [
                    { text: `${data?.map(item => item?.name?.toUpperCase())?.join(', ')} ${data?.length > 1 ? 'veulent' : 'veut'} se battre` }
                ];
            }

            const newSteps = data?.reduce((acc, item) => {
                const getEfficacity = (value) => {
                    if (value > 50) {
                        return translate('C\'est super efficace!');
                    }
                    else if (value > 25) {

                    }
                    else {
                        return translate('Ce n\'est pas très efficace...');
                    }
                };

                let items = [
                    {
                        text: `${item?.name?.toUpperCase()} lance ${skills.getSkill(item?.types[0]?.name, 'enemy')?.toUpperCase()}`,
                        damage: 'user',
                        project_id: item?.group_by_id
                    }
                ];
                let tmpText = getEfficacity(((stats?.total / (workingDaysCount * 8)) * 100) / stats?.data?.length);
                if (tmpText) {
                    items.push({ text: tmpText });
                }
                items.push({
                    text: `${account?.firstname?.toUpperCase()} lance ${skills.getSkill(item?.types[0]?.name, 'team')?.toUpperCase()}`,
                    damage: 'project',
                    value: item?.value,
                    project_id: item?.group_by_id
                });
                tmpText = getEfficacity((item?.value / stats?.total) * 100);
                if (tmpText) {
                    items.push({ text: tmpText });
                }

                projectsHp[item?.group_by_id] = 100;

                lastStepText = lastStepText + `\n${item?.name?.toUpperCase()}: ${item?.value}h (${item?.types?.map(type => type?.name)?.join(', ')})`;

                return [...acc, ...items];
            }, defaultSteps);

            setSteps(newSteps || []);
            setCurrentStep(0);
            if (newSteps?.length > 0) {
                setText(newSteps[0]?.text);
            }
            else {
                setText(
                    <Box sx={{ fontSize: '25px' }}>
                        <Box sx={{ display: 'inline', fontStyle: 'italic' }}>
                            "Durant un moment, il ne se produisit rien. Puis, au bout d'une seconde à peu près, il continua de ne rien se produire."
                        </Box>
                        <Box sx={{ display: 'inline', fontSize: '20px', color: '#C3C3C3' }}>
                            (H2G2)
                        </Box>
                    </Box>
                );
            }
            setHp({ user: 100, projects: projectsHp });

            if (lastStepText) {
                newSteps?.push({
                    text: <Box sx={{ fontSize: '30px', lineHeight: '35px' }}>{`Résumé${lastStepText}\n\nTotal: ${stats?.total}h\nCapacité totale: ${workingDaysCount * 8}h (${workingDaysCount} jours)`}</Box>
                });
            }

            setLoading(false);
        }

        fetchStats();
    }, [stats]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (currentStep < steps?.length && steps[currentStep]) {
            const step = steps[currentStep];
            setText(step.text);

            setTimeout(() => {
                if (step?.damage === 'project') {
                    setAttackAnimation({ type: 'user', id: account?.id })
                }
                else if (step?.damage === 'user') {
                    setAttackAnimation({ type: 'project', id: step?.project_id });
                }

                setTimeout(() => {
                    setAttackAnimation(false);
                    if (step?.damage === 'project') {
                        setFlashAnimation({ type: 'project', id: step?.project_id });
                        const damage = hp?.projects[step?.project_id] - ((step?.value / stats?.total) * 100);
                        setHp(state => ({ ...state, 'projects': { ...state?.projects, [step?.project_id]: damage } }));
                    }
                    else if (step?.damage === 'user') {
                        setFlashAnimation({ type: 'user', id: account?.id });
                        const damage = hp?.user - (((stats?.total / (workingDaysCount * 8)) * 100) / stats?.data?.length);
                        setHp(state => ({ ...state, 'user': damage }));
                    }

                    setTimeout(() => {
                        setFlashAnimation(false);
                    }, 3000);
                }, 1000);
            }, 500);
        }
    }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleNext = () => {
        setCurrentStep(state => state + 1);
    };

    const handleSkip = () => {
        setCurrentStep(steps?.length - 1);

        const projectsHp = stats?.data?.reduce((acc, item) => {
            return { ...acc, [item?.group_by_id]: 100 - (item?.value / stats?.total) * 100 };
        }, {});

        setHp({
            user: 100 - ((stats?.total / (workingDaysCount * 8)) * 100),
            projects: projectsHp
        });
    };

    const max = stats?.data?.length > 0 ? stats?.data[0]?.value / stats?.total : 0;
    const min = stats?.data?.length > 0 ? stats?.data[stats?.data?.length - 1]?.value / stats?.total : 0;

    return (
        <>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                    <CircularProgress size={60} thickness={8} />
                </Box>
            ) : (
                <Box position="relative">
                    <Box sx={{ position: 'absolute', maxWidth: '50%', bottom: '80px', left: '2%', zIndex: 100 }}>
                        <Box sx={{ display: 'inline-flex', position: 'relative' }}>
                            <HealthBar
                                name={account?.firstname}
                                value={hp?.user}
                                sx={{ top: '-200px', left: '50%' }}
                                tooltipText={`${stats?.total}h/${workingDaysCount * 8}h (${translate('total hours/max capacity')})`}
                            />
                            <Box sx={flashAnimation?.type === 'user' ? animationsSx.flash : attackAnimation?.type === 'user' ? animationsSx.attack : {}}>
                                <Ally trigram={account?.trigram} width="250px" height="250px" />
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ position: 'absolute', maxWidth: '65%', bottom: '160px', right: '50px', zIndex: 99 }}>
                        {stats?.data?.map((project, index) => {
                            const activeFlashAnimation = flashAnimation?.type === 'project' && flashAnimation?.id === project?.group_by_id;
                            const activeAttackAnimation = attackAnimation?.type === 'project' && attackAnimation?.id === project?.group_by_id;
                            const minScale = 0.5;
                            const scale = minScale + (1 - minScale) * Math.max(minScale, Math.min(minScale + (1 - minScale) * ((project?.value / stats?.total) - min) / (max - min), 1));
                            return (
                                <Box key={index} sx={{ display: 'inline-flex', position: 'relative' }}>
                                    <HealthBar
                                        name={project?.name}
                                        value={hp?.projects[project?.group_by_id]}
                                        tooltipText={
                                            <>
                                                {`${project?.value}h/${stats?.total}h (${translate('hours worked/total hours')})`}<br />
                                                {`${translate('Most commonly used types')}: ${project?.types?.map(type => type?.name)?.join(', ')}`}
                                            </>
                                        }
                                    />
                                    <Box sx={{ transform: `scale(${scale})` }}>
                                        <Box sx={activeFlashAnimation ? animationsSx.flash : activeAttackAnimation ? animationsSx.attack : {}}>
                                            <Enemy color={project?.color} name={project?.name} />
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                    <Background />
                    <DialogBox onNext={handleNext} onSkip={handleSkip} nextDisabled={currentStep >= steps?.length - 1} skipDisabled={currentStep >= steps?.length - 1}>
                        {text}
                    </DialogBox>
                </Box >
            )}
        </>
    );
};

export default Solo;